/* eslint-disable max-statements */
import { Box, Button, Flex, Group, Paper, Table } from '@mantine/core';
import ChartsEmbedSDK from '@mongodb-js/charts-embed-dom';
import { useEffect, useState } from 'react';

import { documentIcon, downloadIcon, refreshIcon } from '@assets';
import { IconBox } from '@components';
import { downloadChartDataAsExcel, sortChartDataByCreatedAt } from '../../helper';
import classes from './MongoDashboardChartDetails.module.css';

interface MongoDashboardChartDetailsProps {
  chart: any;
  token: string;
  chartsBaseUrl: string;
  onHide(): void;
}

type SDKChartDataFields = Record<string, string>;
interface SDKChartData {
  fields: SDKChartDataFields;
  documents: SDKChartDataFields[];
}

export const MongoDashboardChartDetails: React.FC<MongoDashboardChartDetailsProps> = ({ chart, token, chartsBaseUrl, onHide }) => {
  const [shouldDisplayTheTable, setShouldDisplayTheTable] = useState(false);
  const [chartData, setChartData] = useState({});
  const [sdkChart, setSdkChart] = useState(null);
  const { order } = chart;

  const renderChart = async () => {
    if (document.getElementById(`modal-chart-${order}`) && sdkChart) {
      await sdkChart.render(document.getElementById(`modal-chart-${order}`));
    }
  };

  const refreshChart = () => {
    if (sdkChart) {
      sdkChart.refresh();
    }
  };

  const toggleChartData = async () => {
    if (sdkChart) {
      if (!shouldDisplayTheTable) {
        const data = await sdkChart.getData();
        sortChartDataByCreatedAt(data);
        setChartData(data);
      } else {
        setChartData([]);
      }
      setShouldDisplayTheTable(!shouldDisplayTheTable);
    }
  };

  const downloadChartDataAsXlsx = async () => {
    if (sdkChart) {
      const data: SDKChartData = await sdkChart.getData();
      downloadChartDataAsExcel(data, chart.name);
    }
  };

  useEffect(() => {
    const sdk = new ChartsEmbedSDK({
      baseUrl: chartsBaseUrl,
      getUserToken: () => token,
    });

    setSdkChart(
      sdk.createChart({
        chartId: chart.chartId,
        height: '700px',
      }),
    );
  }, [token, chartsBaseUrl]);

  useEffect(() => {
    renderChart();
  }, [sdkChart]);

  return (
    <>
      <Flex>
        <Box w={'60%'} id={`modal-chart-${order}`}></Box>
        <Box className={classes.actions}>
          <Box className={classes.textWithIcon} onClick={refreshChart}>
            <IconBox icon={refreshIcon} alt={'refresh'} />
            <label>Refresh chart</label>
          </Box>
          <Box className={classes.textWithIcon} onClick={downloadChartDataAsXlsx}>
            <IconBox icon={downloadIcon} alt={'download'} />
            <label>Export as excel</label>
          </Box>
          <Box className={classes.textWithIcon} onClick={toggleChartData}>
            <IconBox icon={documentIcon} alt={'show-chart-data'} />
            <label>{!shouldDisplayTheTable ? 'Show' : 'Hide'} chart data</label>
          </Box>
          {shouldDisplayTheTable && !!Object.keys(chartData).length && (
            <Paper className={classes.tableContainer}>
              <Table striped highlightOnHover withTableBorder withColumnBorders className={classes.table}>
                <Table.Thead>
                  <Table.Tr className={classes.header}>
                    {Object.values((chartData as any).fields).map((column, index) => (
                      <Table.Th className={classes.headerColumn} key={chart.name + chart.order + index} miw={200} maw={300}>
                        {column as any}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(chartData as any).documents.map((line, index) => (
                    <Table.Tr className={classes.line} key={chart.name + chart.order + index}>
                      {Object.values(line).map((column, index2) => (
                        <Table.Td className={classes.lineColumn} key={chart.name + chart.order + index2} miw={200} maw={300}>
                          {column.toString()}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </Box>
      </Flex>
      <Group justify="center" mt="xl">
        <Button type="button" key="cancel-batch-modal-btn" onClick={onHide} size="lg" variant="transparent">
          Close
        </Button>
      </Group>
    </>
  );
};
